import './veterans-portal.css';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';
import { authFetch, getDomain } from '../utils/apis/api';

const BASE_URL = getDomain();

const portalTabs = [
  { id: 'kathleen', label: 'Kathleen Check-In' },
  { id: 'jobs', label: 'Job Assistance' },
  { id: 'staff', label: 'Meet the Staff' },
  { id: 'work-orders', label: 'Work Orders' },
  { id: 'reviews', label: 'Reviews' },
];

const kathleenMoods = [
  {
    id: 'low',
    emoji: '☹',
    title: 'I am having a hard day',
    prompt: 'I am feeling sad today and would like to chat for a little support.',
  },
  {
    id: 'okay',
    emoji: '😐',
    title: 'I am doing okay',
    prompt: 'I am feeling okay and just want to share what is on my mind.',
  },
  {
    id: 'good',
    emoji: '🙂',
    title: 'I am in a good mood',
    prompt: 'I am feeling pretty good today and would like to chat about something light.',
  },
  {
    id: 'grateful',
    emoji: '😊',
    title: 'I want to share something positive',
    prompt: 'I am feeling grateful and want to tell about something positive from my day.',
  },
];

const jobAssistanceActions = [
  {
    id: 'resume',
    emoji: '📄',
    title: 'Help with Resume',
    prompt:
      'Give me 10 resume tips as a numbered markdown list. Then ask me 5 short questions, each on its own numbered line, so I can answer and you can suggest improvements.',
    mode: 'text',
  },
  {
    id: 'interview',
    emoji: '🎙',
    title: 'Practice Interviewing with Kathleen',
    prompt: 'Kathleen, can you help me practice for a job interview?',
    mode: 'voice',
  },
];

const emptyWorkOrderForm = {
  name: '',
  property: '',
  description: '',
  priority: 'Medium',
  category: 'Maintenance',
  date: '',
};

const emptyReviewForm = {
  overall_rating: 5,
  parking_availability: 5,
  grounds_cleanliness: 5,
  maintenance_staff: 5,
  noise: 5,
  construction_quality: 5,
  property_staff: 5,
  would_recommend: true,
  review_title: '',
  review_text: '',
  terms_accepted: false,
  authorize_publish: false,
};

const ratingFields = [
  { name: 'overall_rating', label: 'Overall' },
  { name: 'parking_availability', label: 'Parking' },
  { name: 'grounds_cleanliness', label: 'Grounds' },
  { name: 'maintenance_staff', label: 'Maintenance' },
  { name: 'noise', label: 'Noise' },
  { name: 'construction_quality', label: 'Construction' },
  { name: 'property_staff', label: 'Property Staff' },
];

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'TS';

const VeteransPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('kathleen');
  const [staff, setStaff] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(true);
  const [staffError, setStaffError] = useState('');
  const [workOrderForm, setWorkOrderForm] = useState(emptyWorkOrderForm);
  const [reviewForm, setReviewForm] = useState(emptyReviewForm);
  const [isSubmittingWorkOrder, setIsSubmittingWorkOrder] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [portalMessage, setPortalMessage] = useState(null);

  const handleKathleenPrompt = (prompt) => {
    sessionStorage.setItem('pendingChatPrompt', prompt);
    navigate('/chat');
  };

  const handleKathleenVoicePrompt = (prompt) => {
    sessionStorage.setItem('pendingChatPrompt', prompt);
    sessionStorage.setItem('openVoiceChat', 'true');
    sessionStorage.setItem('voicePromptHint', `Start by saying: ${prompt}`);
    navigate('/chat');
  };

  const fetchStaff = useCallback(async () => {
    setIsLoadingStaff(true);
    setStaffError('');

    try {
      const res = await authFetch(`${BASE_URL}staff`, {
        headers: authHeaders(),
      });

      if (res.status === 401 || res.status === 403) {
        navigate('/');
        return;
      }

      if (!res.ok) throw new Error();

      const data = await res.json();
      setStaff(data);
    } catch {
      setStaffError('Unable to load staff information right now.');
    } finally {
      setIsLoadingStaff(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');

    if (!token) {
      navigate('/');
      return;
    }

    fetchStaff();
  }, [fetchStaff, navigate]);

  const showPortalMessage = (text, type = 'success') => {
    setPortalMessage({ text, type });
    setTimeout(() => setPortalMessage(null), 3500);
  };

  const handleWorkOrderSubmit = async (e) => {
    e.preventDefault();

    if (
      !workOrderForm.name.trim() ||
      !workOrderForm.property.trim() ||
      !workOrderForm.description.trim() ||
      !workOrderForm.priority.trim() ||
      !workOrderForm.category.trim() ||
      !workOrderForm.date.trim()
    ) {
      return;
    }

    setIsSubmittingWorkOrder(true);

    try {
      const res = await authFetch(`${BASE_URL}work-orders`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...workOrderForm,
          name: workOrderForm.name.trim(),
          property: workOrderForm.property.trim(),
          description: workOrderForm.description.trim(),
          priority: workOrderForm.priority.trim(),
          category: workOrderForm.category.trim(),
          date: workOrderForm.date.trim(),
        }),
      });

      if (!res.ok) throw new Error();

      setWorkOrderForm(emptyWorkOrderForm);
      showPortalMessage('Work order submitted.');
    } catch {
      showPortalMessage('Unable to submit work order.', 'error');
    } finally {
      setIsSubmittingWorkOrder(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (
      !reviewForm.review_title.trim() ||
      !reviewForm.review_text.trim() ||
      !reviewForm.terms_accepted ||
      !reviewForm.authorize_publish
    ) {
      return;
    }

    setIsSubmittingReview(true);

    try {
      const res = await authFetch(`${BASE_URL}reviews`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...reviewForm,
          review_title: reviewForm.review_title.trim(),
          review_text: reviewForm.review_text.trim(),
        }),
      });

      if (!res.ok) throw new Error();

      setReviewForm(emptyReviewForm);
      showPortalMessage('Review submitted.');
    } catch {
      showPortalMessage('Unable to submit review.', 'error');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <>
      <Header onBurgerClick={() => { }} />
      <main className="vp-page">
        <section className="vp-shell">
          <div className="vp-header">
            <div>
              <p className="vp-kicker">Veterans Portal</p>
              <h1 className="vp-title">Your Tranquilla support hub</h1>
            </div>
          </div>

          <div className="vp-tabs" role="tablist">
            {portalTabs.map((tab) => (
              <button
                key={tab.id}
                className={`vp-tab${activeTab === tab.id ? ' vp-tab-active' : ''}`}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {portalMessage && (
            <div className={`vp-message vp-message-${portalMessage.type}`}>
              {portalMessage.text}
            </div>
          )}

          {activeTab === 'kathleen' && (
            <section className="vp-panel">
              <div className="vp-panel-header">
                <div>
                  <h2 className="vp-panel-title">Chat with Kathleen</h2>
                  <p className="vp-panel-subtitle">
                    Choose the option closest to how you feel and start with a simple prompt.
                  </p>
                </div>
              </div>

              <div className="vp-kathleen-grid">
                {kathleenMoods.map((mood) => (
                  <button
                    className={`vp-mood-card vp-mood-${mood.id}`}
                    key={mood.id}
                    type="button"
                    onClick={() => handleKathleenPrompt(mood.prompt)}
                  >
                    <span className="vp-mood-emoji">{mood.emoji}</span>
                    <span className="vp-mood-copy">
                      <span className="vp-mood-title">{mood.title}</span>
                      <span className="vp-mood-prompt">{mood.prompt}</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'jobs' && (
            <section className="vp-panel">
              <div className="vp-panel-header">
                <div>
                  <h2 className="vp-panel-title">Job Assistance</h2>
                  <p className="vp-panel-subtitle">
                    Get resume support or practice interviewing with Kathleen.
                  </p>
                </div>
              </div>

              <div className="vp-kathleen-grid">
                {jobAssistanceActions.map((action) => (
                  <button
                    className={`vp-mood-card vp-job-${action.id}`}
                    key={action.id}
                    type="button"
                    onClick={() =>
                      action.mode === 'voice'
                        ? handleKathleenVoicePrompt(action.prompt)
                        : handleKathleenPrompt(action.prompt)
                    }
                  >
                    <span className="vp-mood-emoji">{action.emoji}</span>
                    <span className="vp-mood-copy">
                      <span className="vp-mood-title">{action.title}</span>
                      <span className="vp-mood-prompt">{action.prompt}</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'staff' && (
            <section className="vp-panel">
              <div className="vp-panel-header">
                <div>
                  <h2 className="vp-panel-title">Meet the Staff</h2>
                  <p className="vp-panel-subtitle">
                    The people available to help with support, services, and community needs.
                  </p>
                </div>
                <span className="vp-count">{staff.length}</span>
              </div>

              {isLoadingStaff && (
                <div className="vp-state">Loading staff...</div>
              )}

              {!isLoadingStaff && staffError && (
                <div className="vp-state vp-state-error">{staffError}</div>
              )}

              {!isLoadingStaff && !staffError && staff.length === 0 && (
                <div className="vp-state">No staff profiles are available yet.</div>
              )}

              {!isLoadingStaff && !staffError && staff.length > 0 && (
                <div className="vp-staff-grid">
                  {staff.map((member) => (
                    <article className="vp-staff-card" key={member.id}>
                      {member.profile_picture_url ? (
                        <img
                          className="vp-staff-photo"
                          src={member.profile_picture_url}
                          alt={member.name}
                        />
                      ) : (
                        <div className="vp-staff-initials">
                          {getInitials(member.name)}
                        </div>
                      )}

                      <div className="vp-staff-body">
                        <h3 className="vp-staff-name">{member.name}</h3>
                        <p className="vp-staff-role">{member.designation}</p>
                        <p className="vp-staff-description">
                          {member.description}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}

          {activeTab === 'work-orders' && (
            <section className="vp-panel">
              <div className="vp-panel-header">
                <div>
                  <h2 className="vp-panel-title">Submit a Work Order</h2>
                  <p className="vp-panel-subtitle">
                    Send maintenance and service requests to the Tranquilla team.
                  </p>
                </div>
              </div>

              <form className="vp-form" onSubmit={handleWorkOrderSubmit}>
                <div className="vp-form-grid">
                  <label className="vp-field">
                    <span>Name</span>
                    <input
                      type="text"
                      value={workOrderForm.name}
                      onChange={(e) =>
                        setWorkOrderForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      required
                    />
                  </label>

                  <label className="vp-field">
                    <span>Property</span>
                    <input
                      type="text"
                      value={workOrderForm.property}
                      onChange={(e) =>
                        setWorkOrderForm((prev) => ({
                          ...prev,
                          property: e.target.value,
                        }))
                      }
                      required
                    />
                  </label>

                  <label className="vp-field">
                    <span>Priority</span>
                    <select
                      value={workOrderForm.priority}
                      onChange={(e) =>
                        setWorkOrderForm((prev) => ({
                          ...prev,
                          priority: e.target.value,
                        }))
                      }
                    >
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                      <option>Urgent</option>
                    </select>
                  </label>

                  <label className="vp-field">
                    <span>Category</span>
                    <select
                      value={workOrderForm.category}
                      onChange={(e) =>
                        setWorkOrderForm((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                    >
                      <option>Maintenance</option>
                      <option>Plumbing</option>
                      <option>Electrical</option>
                      <option>Grounds</option>
                      <option>Safety</option>
                      <option>Other</option>
                    </select>
                  </label>

                  <label className="vp-field">
                    <span>Date</span>
                    <input
                      type="date"
                      value={workOrderForm.date}
                      onChange={(e) =>
                        setWorkOrderForm((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                </div>

                <label className="vp-field">
                  <span>Description</span>
                  <textarea
                    rows={5}
                    value={workOrderForm.description}
                    onChange={(e) =>
                      setWorkOrderForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <button
                  className="vp-primary-btn"
                  type="submit"
                  disabled={isSubmittingWorkOrder}
                >
                  {isSubmittingWorkOrder ? 'Submitting...' : 'Submit work order'}
                </button>
              </form>
            </section>
          )}

          {activeTab === 'reviews' && (
            <section className="vp-panel">
              <div className="vp-panel-header">
                <div>
                  <h2 className="vp-panel-title">Submit a Review</h2>
                  <p className="vp-panel-subtitle">
                    Share feedback about the property, services, and community experience.
                  </p>
                </div>
              </div>

              <form className="vp-form" onSubmit={handleReviewSubmit}>
                <div className="vp-rating-grid">
                  {ratingFields.map((field) => (
                    <label className="vp-field" key={field.name}>
                      <span>{field.label}</span>
                      <select
                        value={reviewForm[field.name]}
                        onChange={(e) =>
                          setReviewForm((prev) => ({
                            ...prev,
                            [field.name]: Number(e.target.value),
                          }))
                        }
                      >
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <option key={rating} value={rating}>
                            {rating} *
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>

                <label className="vp-field">
                  <span>Review Title</span>
                  <input
                    type="text"
                    maxLength={255}
                    value={reviewForm.review_title}
                    onChange={(e) =>
                      setReviewForm((prev) => ({
                        ...prev,
                        review_title: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label className="vp-field">
                  <span>Review</span>
                  <textarea
                    rows={5}
                    value={reviewForm.review_text}
                    onChange={(e) =>
                      setReviewForm((prev) => ({
                        ...prev,
                        review_text: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label className="vp-checkbox">
                  <input
                    type="checkbox"
                    checked={reviewForm.would_recommend}
                    onChange={(e) =>
                      setReviewForm((prev) => ({
                        ...prev,
                        would_recommend: e.target.checked,
                      }))
                    }
                  />
                  <span>I would recommend living here.</span>
                </label>

                <label className="vp-checkbox">
                  <input
                    type="checkbox"
                    checked={reviewForm.terms_accepted}
                    onChange={(e) =>
                      setReviewForm((prev) => ({
                        ...prev,
                        terms_accepted: e.target.checked,
                      }))
                    }
                    required
                  />
                  <span>I accept the ratings and reviews terms.</span>
                </label>

                <label className="vp-checkbox">
                  <input
                    type="checkbox"
                    checked={reviewForm.authorize_publish}
                    onChange={(e) =>
                      setReviewForm((prev) => ({
                        ...prev,
                        authorize_publish: e.target.checked,
                      }))
                    }
                    required
                  />
                  <span>I authorize Tranquilla to publish this review.</span>
                </label>

                <button
                  className="vp-primary-btn"
                  type="submit"
                  disabled={
                    isSubmittingReview ||
                    !reviewForm.terms_accepted ||
                    !reviewForm.authorize_publish
                  }
                >
                  {isSubmittingReview ? 'Submitting...' : 'Submit review'}
                </button>
              </form>
            </section>
          )}
        </section>
      </main>
    </>
  );
};

export default VeteransPortal;
